// Mission step API service.
//
// Talks to the Laravel Admin Mission Steps / Journey Mission Steps endpoints
// so the User App's mission-step UI (missionSteps.js / shopmap.js) is driven
// by Admin-configured data instead of the hardcoded MISSION_STEPS array.
// Requests go through apiClient so they carry the Dealzta bearer token when
// one is stored, same as shopService.js / journeyService.js / rewardService.js.

import { fetchJson } from './apiClient';

// Genuine-API-failure fallback ONLY (mirrors shopmap.js's FALLBACK_SHOPS
// pattern). Never used when Laravel responds successfully, even with an
// empty list — only when a request itself fails (network error / non-ok
// response). Shaped like real mission_steps rows so callers can't tell the
// difference.
const FALLBACK_MISSION_STEPS = [
  {
    id: 1,
    step_key: 'discover',
    step_number: 1,
    title: 'Discover the cafe.',
    subtitle: null,
    hint: null,
    cta_label: null,
    icon_key: 'pin',
    color: '#6c4ce8',
    active: true,
  },
  {
    id: 2,
    step_key: 'selfie',
    step_number: 2,
    title: 'Take a selfie with Zini.',
    subtitle: null,
    hint: null,
    cta_label: null,
    icon_key: 'camera',
    color: '#ff9f2e',
    active: true,
  },
  {
    id: 3,
    step_key: 'post',
    step_number: 3,
    title: 'Post it with #FoundByZini and tag the cafe + DealZta.',
    subtitle: null,
    hint: null,
    cta_label: null,
    icon_key: 'phone',
    color: '#ff3d81',
    active: true,
  },
  {
    id: 4,
    step_key: 'reward',
    step_number: 4,
    title: 'Collect the points and unlock your surprise deal!',
    subtitle: null,
    hint: null,
    cta_label: null,
    icon_key: 'gift',
    color: '#17b8a6',
    active: true,
  },
];

async function fetchList(path) {
  const data = await fetchJson(path);
  return Array.isArray(data) ? data : [];
}

/**
 * Fetch the full mission-step list from the Laravel Admin API
 * (GET /admin/mission-steps). Resolves to the raw array of mission-step
 * objects on success (may be empty), throws on a genuine API/network
 * failure.
 */
export async function getMissionSteps() {
  return fetchList('/mission-steps');
}

/**
 * Fetch the full journey <-> mission-step relationship list from the
 * Laravel Admin API (GET /admin/journey-mission-steps). Resolves to the raw
 * array of relationship objects on success (may be empty), throws on a
 * genuine API/network failure.
 */
export async function getJourneyMissionSteps() {
  return fetchList('/journey-mission-steps');
}

/**
 * Order mission steps by their Admin-configured step_number (stable sort —
 * ties keep the order they arrived in).
 */
function sortByStepNumber(steps) {
  return steps
    .map((step, index) => ({ step, index }))
    .sort((a, b) => {
      const diff = (a.step.step_number ?? 0) - (b.step.step_number ?? 0);
      return diff !== 0 ? diff : a.index - b.index;
    })
    .map(({ step }) => step);
}

/**
 * Fetch mission steps and reduce them to what the User App shows globally:
 * only active steps, ordered by step_number. Used when no specific journey
 * context is available.
 */
export async function getActiveMissionSteps() {
  try {
    const steps = await getMissionSteps();
    return sortByStepNumber(steps.filter((step) => step.active === true));
  } catch (err) {
    console.error('missionStepService: failed to load mission steps from Laravel, using fallback list.', err);
    return sortByStepNumber(FALLBACK_MISSION_STEPS.filter((step) => step.active === true));
  }
}

/**
 * Fetch the mission steps configured for a specific journey.
 *
 * Uses BOTH Laravel resources: active mission_steps are matched against
 * journey_mission_steps rows where journey_id === journeyId (matching on
 * journey_mission_steps.mission_step_id === mission_steps.id). A journey
 * with no configured relationships resolves to [] — every other journey's
 * steps are never used as a stand-in.
 *
 * Ordered by journey_mission_steps.step_order; when step_order values are
 * equal/default, mission_steps.step_number is the stable fallback order.
 */
export async function getMissionStepsForJourney(journeyId) {
  try {
    const [steps, relationships] = await Promise.all([
      getMissionSteps(),
      getJourneyMissionSteps(),
    ]);

    const activeStepsById = new Map(
      steps.filter((step) => step.active === true).map((step) => [step.id, step])
    );

    const journeyRelationships = relationships.filter((rel) => rel.journey_id === journeyId);

    const matched = journeyRelationships
      .map((rel, index) => {
        const step = activeStepsById.get(rel.mission_step_id);
        if (!step) return null;
        return {
          ...step,
          step_order: rel.step_order,
          journey_id: rel.journey_id,
          _relIndex: index,
        };
      })
      .filter(Boolean);

    matched.sort((a, b) => {
      const orderA = a.step_order ?? a.step_number ?? 0;
      const orderB = b.step_order ?? b.step_number ?? 0;
      if (orderA !== orderB) return orderA - orderB;
      const numberDiff = (a.step_number ?? 0) - (b.step_number ?? 0);
      return numberDiff !== 0 ? numberDiff : a._relIndex - b._relIndex;
    });

    return matched.map(({ _relIndex, ...step }) => step);
  } catch (err) {
    console.error('missionStepService: failed to load journey mission steps from Laravel, using fallback list.', err);
    return sortByStepNumber(FALLBACK_MISSION_STEPS.filter((step) => step.active === true))
      .map((step) => ({ ...step, journey_id: journeyId }));
  }
}
